import React, { useEffect, useState } from "react";
import axios from "axios";
import { Offcanvas, Spinner } from "react-bootstrap";
import {
  BagCheck,
  Calendar3,
  ChatLeftText,
  ExclamationCircle,
  GeoAlt,
  Person,
  Receipt,
  StarFill,
} from "react-bootstrap-icons";

const FeedbackDetailDrawer = ({
  show,
  onHide,
  feedbackId,
  accessToken,
  apiBaseUrl,
}) => {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ---------------------------------------------------------------------- */
  /* HELPERS                                                                */
  /* ---------------------------------------------------------------------- */

  const getHeaders = () => {
    if (!accessToken) return {};

    return {
      Authorization: `Bearer ${accessToken}`,
    };
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const normalizedValue = String(value).includes("T")
      ? value
      : String(value).replace(" ", "T");

    const date = new Date(normalizedValue);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatMoney = (value) => {
    const number = Number(value || 0);

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(number);
  };

  const getInitial = () => {
    const name = feedback?.user?.name || "Guest";

    return name.charAt(0).toUpperCase();
  };

  /* ---------------------------------------------------------------------- */
  /* FETCH                                                                  */
  /* ---------------------------------------------------------------------- */

  const fetchFeedbackDetail = async () => {
    if (!feedbackId) return;

    try {
      setLoading(true);
      setError("");
      setFeedback(null);

      const response = await axios.get(
        `${apiBaseUrl}/feedbacks/${feedbackId}`,
        {
          headers: getHeaders(),
        },
      );

      if (response?.data?.status === true) {
        setFeedback(response?.data?.data || null);
      } else {
        setError("Unable to load feedback details.");
      }
    } catch (err) {
      console.error("Feedback detail error:", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load feedback details.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && feedbackId) {
      fetchFeedbackDetail();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, feedbackId]);

  /* ---------------------------------------------------------------------- */
  /* RATINGS                                                                */
  /* ---------------------------------------------------------------------- */

  const ratings = feedback
    ? [
        {
          label: "Overall Experience",
          value: feedback.overall_experience,
        },
        {
          label: "Taste",
          value: feedback?.menu?.taste,
        },
        {
          label: "Presentation",
          value: feedback?.menu?.presentation,
        },
        {
          label: "Freshness",
          value: feedback?.menu?.freshness,
        },
        {
          label: "Menu Variety",
          value: feedback?.menu?.variety,
        },
        {
          label: "Value for Money",
          value: feedback?.menu?.value_for_money,
        },
        {
          label: "Cleanliness",
          value: feedback.cleanliness,
        },
        {
          label: "Staff Friendliness",
          value: feedback.staff_friendliness,
        },
        {
          label: "Recommendation",
          value: feedback.recommend_rating,
        },
      ]
    : [];

  return (
    <>
      <Offcanvas
        show={show}
        onHide={onHide}
        placement="end"
        className="feedback-detail-drawer"
        backdrop
        scroll={false}
      >
        <Offcanvas.Header closeButton>
          <div>
            <span className="drawer-eyebrow">Customer Feedback</span>

            <Offcanvas.Title>Feedback Details</Offcanvas.Title>
          </div>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {loading && (
            <div className="drawer-loading">
              <Spinner animation="border" size="sm" />

              <span>Loading feedback...</span>
            </div>
          )}

          {!loading && error && (
            <div className="drawer-error">
              <ExclamationCircle size={18} />

              <span>{error}</span>
            </div>
          )}

          {!loading && !error && feedback && (
            <>
              {/* CUSTOMER */}

              <section className="drawer-customer-card">
                <div className="drawer-avatar">{getInitial()}</div>

                <div className="drawer-customer-info">
                  <h4>{feedback?.user?.name || "Guest"}</h4>

                  <div className="drawer-customer-meta">
                    {feedback?.user?.mobile && (
                      <span>
                        <Person size={12} />

                        {feedback.user.mobile}
                      </span>
                    )}

                    <span>
                      <GeoAlt size={12} />

                      {feedback?.outlet?.name || "Unknown Outlet"}
                    </span>
                  </div>
                </div>

                <SourceBadge source={feedback?.source} />
              </section>

              {/* OVERALL */}

              <section className="drawer-overall-card">
                <div>
                  <span className="drawer-section-eyebrow">
                    Overall experience
                  </span>

                  <h3>Customer Experience</h3>
                </div>

                <div className="drawer-overall-rating">
                  <StarFill size={15} />

                  <strong>
                    {Number(feedback?.overall_experience || 0).toFixed(1)}
                  </strong>

                  <span>/ 5</span>
                </div>
              </section>

              {/* INFO */}

              <section className="drawer-section">
                <div className="drawer-section-title">
                  <Receipt size={15} />
                  Feedback Information
                </div>

                <div className="drawer-info-grid">
                  <InfoItem
                    label="Outlet"
                    value={feedback?.outlet?.name || "Unknown Outlet"}
                  />

                  <InfoItem
                    label="Submitted"
                    value={formatDateTime(feedback?.submitted_at)}
                  />

                  {feedback?.order?.meta?.order_no && (
                    <InfoItem
                      label="Order No."
                      value={`#${feedback?.order?.meta?.order_sequence || ""}${feedback?.order?.meta?.order_no || ""}`.trim()}
                    />
                  )}

                  {feedback?.order?.amount !== undefined && (
                    <InfoItem
                      label="Order Amount"
                      value={formatMoney(feedback?.order?.amount)}
                    />
                  )}

                  {feedback?.order?.placed_at && (
                    <InfoItem
                      label="Order Placed"
                      value={formatDateTime(feedback.order.placed_at)}
                    />
                  )}

                  <InfoItem
                    label="Source"
                    value={feedback?.source === "order" ? `${feedback.order.placed_at}` : "Walk-in"}
                  />
                </div>
              </section>

              {/* RATINGS */}

              <section className="drawer-section">
                <div className="drawer-section-header">
                  <div className="drawer-section-title mb-0">
                    <StarFill size={14} />
                    Experience Ratings
                  </div>

                  <span className="drawer-rating-count">
                    {ratings.length} categories
                  </span>
                </div>

                <div className="drawer-rating-list">
                  {ratings.map((rating) => (
                    <RatingRow
                      key={rating.label}
                      label={rating.label}
                      value={rating.value}
                    />
                  ))}
                </div>
              </section>

              {/* SUGGESTION */}

              {feedback?.suggestions && (
                <section className="drawer-section drawer-suggestion-section">
                  <div className="drawer-section-title">
                    <ChatLeftText size={15} />
                    Customer Suggestion
                  </div>

                  <div className="drawer-suggestion">
                    <span className="drawer-quote">“</span>

                    <p>{feedback.suggestions}</p>
                  </div>
                </section>
              )}

              {/* PRODUCTS */}

              {feedback?.items?.length > 0 && (
                <section className="drawer-section">
                  <div className="drawer-section-header">
                    <div className="drawer-section-title mb-0">
                      <BagCheck size={15} />
                      Rated Items
                    </div>

                    <span className="drawer-rating-count">
                      {feedback.items.length} item
                      {feedback.items.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  <div className="drawer-products">
                    {feedback.items.map((item, index) => (
                      <ProductCard
                        key={item.id || item.product_id || index}
                        item={item}
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <style>
        {`
                .feedback-detail-drawer {
                    width: 520px !important;
                    max-width: 100vw;
                    border-left: 0 !important;
                    box-shadow: -14px 0 45px rgba(37,112,180,.12);
                }

                .feedback-detail-drawer .offcanvas-header {
                    padding: 20px 22px 17px;
                    border-bottom: 1px solid #EAF2FB;
                    background: #ffffff;
                }

                .feedback-detail-drawer .offcanvas-title {
                    color: #071C3A;
                    font-size: 20px;
                    font-weight: 800;
                    margin-top: 2px;
                }

                .feedback-detail-drawer .btn-close {
                    width: 12px;
                    height: 12px;
                    box-shadow: none;
                }

                .drawer-eyebrow,
                .drawer-section-eyebrow {
                    display: block;
                    color: #1677E8;
                    font-size: 9px;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    font-weight: 800;
                }

                .feedback-detail-drawer .offcanvas-body {
                    padding: 18px;
                    background: #F4FAFF;
                }

                .drawer-loading {
                    min-height: 300px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    color: #60748A;
                    font-size: 12px;
                }

                .drawer-error {
                    display: flex;
                    align-items: flex-start;
                    gap: 9px;
                    background: #FFF1EA;
                    border: 1px solid #FFE0D2;
                    border-radius: 11px;
                    color: #F25B2A;
                    padding: 12px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .drawer-customer-card {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 15px;
                    margin-bottom: 13px;
                    background: #ffffff;
                    border: 1px solid #DDEEFF;
                    border-radius: 14px;
                }

                .drawer-avatar {
                    width: 43px;
                    height: 43px;
                    flex: 0 0 43px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #DCEEFF;
                    color: #1677E8;
                    font-size: 15px;
                    font-weight: 800;
                }

                .drawer-customer-info {
                    flex: 1;
                    min-width: 0;
                }

                .drawer-customer-info h4 {
                    color: #102A43;
                    font-size: 14px;
                    font-weight: 800;
                    margin: 0 0 5px;
                }

                .drawer-customer-meta {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 8px 12px;
                }

                .drawer-customer-meta span {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    color: #71869A;
                    font-size: 10px;
                }

                .drawer-source-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 5px 8px;
                    border-radius: 7px;
                    background: #EDF7FF;
                    color: #1677E8;
                    font-size: 9px;
                    font-weight: 800;
                    text-transform: capitalize;
                }

                .drawer-source-badge.walk-in {
                    background: #EAFBF4;
                    color: #08A878;
                }

                .drawer-overall-card {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 15px;
                    padding: 15px 17px;
                    margin-bottom: 13px;
                    border-radius: 14px;
                    border: 1px solid #DDEEFF;
                    background: linear-gradient(
                        110deg,
                        #EDF7FF 0%,
                        #ffffff 75%
                    );
                }

                .drawer-overall-card h3 {
                    color: #071C3A;
                    font-size: 14px;
                    font-weight: 700;
                    margin: 3px 0 0;
                }

                .drawer-overall-rating {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    color: #1677E8;
                }

                .drawer-overall-rating svg {
                    color: #FFB51B;
                }

                .drawer-overall-rating strong {
                    font-size: 24px;
                    line-height: 1;
                }

                .drawer-overall-rating span {
                    color: #71869A;
                    font-size: 10px;
                    margin-top: 6px;
                }

                .drawer-section {
                    background: #ffffff;
                    border: 1px solid #DDEEFF;
                    border-radius: 14px;
                    padding: 15px;
                    margin-bottom: 13px;
                }

                .drawer-section-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    margin-bottom: 14px;
                }

                .drawer-section-title {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    color: #102A43;
                    font-size: 12px;
                    font-weight: 800;
                    margin-bottom: 13px;
                }

                .drawer-section-title svg {
                    color: #1677E8;
                }

                .drawer-rating-count {
                    color: #71869A;
                    font-size: 9px;
                }

                .drawer-info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px 15px;
                }

                .drawer-info-item span {
                    display: block;
                    color: #8AA0B5;
                    font-size: 9px;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                    font-weight: 700;
                    margin-bottom: 3px;
                }

                .drawer-info-item strong {
                    display: block;
                    color: #102A43;
                    font-size: 11px;
                    font-weight: 700;
                    word-break: break-word;
                }

                .drawer-rating-list {
                    display: flex;
                    flex-direction: column;
                }

                .drawer-rating-row {
                    display: grid;
                    grid-template-columns: 145px 1fr 42px;
                    gap: 10px;
                    align-items: center;
                    padding: 8px 0;
                    border-bottom: 1px solid #EAF2FB;
                }

                .drawer-rating-row:last-child {
                    border-bottom: 0;
                }

                .drawer-rating-label {
                    color: #334E68;
                    font-size: 10px;
                    font-weight: 600;
                }

                .drawer-rating-track {
                    position: relative;
                    width: 100%;
                    height: 6px;
                    overflow: hidden;
                    border-radius: 99px;
                    background: #EAF2FB;
                }

                .drawer-rating-fill {
                    height: 100%;
                    border-radius: inherit;
                    transition: width .25s ease;
                }

                .drawer-rating-value {
                    display: inline-flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 3px;
                    font-size: 10px;
                    font-weight: 800;
                }

                .drawer-rating-value svg {
                    color: #FFB51B;
                }

                .drawer-suggestion-section {
                    background: linear-gradient(
                        120deg,
                        #F4FAFF,
                        #ffffff
                    );
                    border-color: #DDEEFF;
                }

                .drawer-suggestion {
                    position: relative;
                    padding: 11px 12px 11px 27px;
                    border-radius: 9px;
                    background: #ffffff;
                    border: 1px solid #DCEEFF;
                }

                .drawer-quote {
                    position: absolute;
                    left: 10px;
                    top: 2px;
                    color: #A7D2F8;
                    font-size: 29px;
                    line-height: 1;
                    font-family: Georgia, serif;
                }

                .drawer-suggestion p {
                    color: #334E68;
                    font-size: 11px;
                    line-height: 1.65;
                    margin: 0;
                }

                .drawer-products {
                    display: flex;
                    flex-direction: column;
                    gap: 9px;
                }

                .drawer-product {
                    padding: 11px 12px;
                    border-radius: 10px;
                    border: 1px solid #EAF2FB;
                    background: #F7FBFF;
                }

                .drawer-product-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 10px;
                }

                .drawer-product-name {
                    color: #102A43;
                    font-size: 11px;
                    font-weight: 800;
                }

                .drawer-product-qty {
                    color: #8AA0B5;
                    font-size: 9px;
                    margin-top: 2px;
                }

                .drawer-product-rating {
                    display: inline-flex;
                    align-items: center;
                    gap: 3px;
                    padding: 4px 6px;
                    border-radius: 6px;
                    background: #FFF4CC;
                    color: #B7791F;
                    font-size: 9px;
                    font-weight: 800;
                }

                .drawer-product-rating svg {
                    color: #FFB51B;
                }

                .drawer-product-comment {
                    margin: 8px 0 0;
                    color: #60748A;
                    font-size: 10px;
                    line-height: 1.55;
                }

                .drawer-addons {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 5px;
                    margin-top: 8px;
                }

                .drawer-addon {
                    display: inline-flex;
                    padding: 4px 7px;
                    border-radius: 6px;
                    background: #EDF7FF;
                    color: #1677E8;
                    font-size: 8px;
                    font-weight: 700;
                }

                @media (max-width: 575px) {
                    .feedback-detail-drawer {
                        width: 100% !important;
                    }

                    .feedback-detail-drawer .offcanvas-header {
                        padding: 16px;
                    }

                    .feedback-detail-drawer .offcanvas-body {
                        padding: 12px;
                    }

                    .drawer-info-grid {
                        grid-template-columns: 1fr;
                    }

                    .drawer-rating-row {
                        grid-template-columns:
                            120px
                            1fr
                            38px;
                        gap: 7px;
                    }

                    .drawer-customer-card {
                        align-items: flex-start;
                    }

                    .drawer-customer-card
                        .drawer-source-badge {
                        position: absolute;
                        right: 12px;
                        top: 12px;
                    }

                    .drawer-customer-info {
                        padding-right: 55px;
                    }
                }
                `}
      </style>
    </>
  );
};

/* ========================================================================== */
/* INFO ITEM                                                                  */
/* ========================================================================== */

const InfoItem = ({ label, value }) => {
  return (
    <div className="drawer-info-item">
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
};

/* ========================================================================== */
/* RATING ROW                                                                 */
/* ========================================================================== */

const RatingRow = ({ label, value }) => {
  const rating = Number(value || 0);

  const percentage = Math.min(100, Math.max(0, (rating / 5) * 100));

  const getColor = () => {
    if (rating >= 4.5) {
      return "#08A878";
    }

    if (rating >= 4) {
      return "#1677E8";
    }

    if (rating >= 3) {
      return "#F4AD12";
    }

    return "#F25B2A";
  };

  return (
    <div className="drawer-rating-row">
      <span className="drawer-rating-label">{label}</span>

      <div className="drawer-rating-track">
        <div
          className="drawer-rating-fill"
          style={{
            width: `${percentage}%`,
            background: getColor(),
          }}
        />
      </div>

      <span
        className="drawer-rating-value"
        style={{
          color: getColor(),
        }}
      >
        <StarFill size={8} />

        {rating ? rating.toFixed(1) : "—"}
      </span>
    </div>
  );
};

/* ========================================================================== */
/* PRODUCT CARD                                                               */
/* ========================================================================== */

const ProductCard = ({ item }) => {
  return (
    <div className="drawer-product">
      <div className="drawer-product-header">
        <div>
          <div className="drawer-product-name">
            {item?.product_name || item?.name || "Product"}
          </div>

          <div className="drawer-product-qty">
            Quantity: {item?.quantity || 1}
          </div>
        </div>

        <div className="drawer-product-rating">
          <StarFill size={8} />

          {Number(item?.rating || 0).toFixed(1)}
        </div>
      </div>

      {item?.comment && (
        <p className="drawer-product-comment">“{item.comment}”</p>
      )}

      {item?.addons?.length > 0 && (
        <div className="drawer-addons">
          {item.addons.map((addon, index) => (
            <span
              className="drawer-addon"
              key={addon.id || addon.addon_id || index}
            >
              {addon.name || addon.addon_name || "Addon"}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========================================================================== */
/* SOURCE BADGE                                                               */
/* ========================================================================== */

const SourceBadge = ({ source }) => {
  const normalizedSource = String(source || "walk-in").toLowerCase();

  const isWalkIn = ["walk-in", "walkin", "outlet"].includes(normalizedSource);

  return (
    <span className={`drawer-source-badge ${isWalkIn ? "walk-in" : ""}`}>
      {isWalkIn ? "Walk-in" : "Order"}
    </span>
  );
};

export default FeedbackDetailDrawer;
